import { getBrowserGeoUri } from "../utils/location";
import dayjs from "../utils/dayjs";
import { parseMessage } from "../utils/messageParser";
import { dstCalculator } from "../utils/date";
import { IHashMap, IModifiedType } from "../sync/sync.types";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { NODEVICEID, NOUSERID } from "./DutyStatusLog";

export type TContentType = "Normal" | "CannedResponse" | "Location" | "GoTalk" | "DriverAuthList" | "IoxOutput" | "Immobilization";

export interface IRoutePlanItem {
    id: string;
    zone: IIdEntity;
}

export interface IRoute extends INamedEntity {
    routePlanItemCollection: IRoutePlanItem[];
}

interface ICannedResponse {
    text: string;
}

export interface IMessageContent {
    message?: string;
    contentType?: TContentType;
    id?: string;
    routeId?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    cannedResponseOptions?: ICannedResponse[];
    relayState?: string;
    urgent?: boolean;
}

export interface IMessage extends IHashMap<IMessage> {
    user?: IIdEntity;
    version?: string;
    isActive?: boolean;
    sent?: string;
    read?: string;
    delivered?: string;
    isDirectionToVehicle: boolean;
    markedReadBy?: IIdEntity;
    parentMessage?: IMessage;
    activeFrom?: string;
    activeTo?: string;
    messageContent?: IMessageContent;
    recipient?: IIdEntity;
    device?: IIdEntity;
    replyMessage?: IIdEntity;

    /* eslint-disable @typescript-eslint/naming-convention */
    _audibleAlert?: boolean;
    _routeInfo?: IMessageContent[];
    _routeName?: string;
    _groupedMessages?: IMessage[];
    _userName?: string;
    _cannedReply?: string;
    _messageLocationUri?: string[];
    _response?: ICannedResponse;
    /* eslint-enable @typescript-eslint/naming-convention */
}

const NEWLINE_REGEX = /\n/g;

export default class Message implements IMessage {
    id: string;
    sent?: string;
    delivered?: string;
    device?: IIdEntity;
    user?: IIdEntity;
    recipient?: IIdEntity;
    messageContent?: IMessageContent;
    isDirectionToVehicle: boolean;
    isActive: boolean;
    version?: string;
    activeFrom?: string;
    activeTo?: string;
    parentMessage?: IMessage;
    replyMessage?: IIdEntity;
    read?: string;
    markedReadBy?: IIdEntity;

    // client-side props
    modified?: IModifiedType;
    previousState?: IMessage;
    /* eslint-disable @typescript-eslint/naming-convention */
    _syncCounter?: number;

    _groupedMessages?: IMessage[];
    _userName?: string;
    _routeInfo?: IMessageContent[];
    _routeName?: string;
    _messageLocationUri?: string[];
    _response?: ICannedResponse;
    _audibleAlert?: boolean;
    _cannedReply?: string;

    constructor(object?: IMessage) {
        this.id = "";
        this.isDirectionToVehicle = false;
        this.isActive = true;
        this.user = { id: NOUSERID };
        this.device = { id: NODEVICEID };
        this.recipient = { id: NOUSERID };
        this.markedReadBy = { id: NOUSERID };

        this._syncCounter = 5;

        if (object && Object.keys(object).length > 0) {
            this.id = object.id ?? "";
            this.sent = object.sent;
            this.delivered = object.delivered;
            this.device = object.device;
            this.user = object.user;
            this.recipient = object.recipient;
            this.messageContent = object.messageContent;
            this.isDirectionToVehicle = object.isDirectionToVehicle ?? false;
            this.isActive = object.isActive ?? true;
            this.parentMessage = object.parentMessage;
            this.replyMessage = object.replyMessage;
            this.read = object.read;
            this.markedReadBy = object.markedReadBy;
            this.version = object.version;
            this.activeFrom = object.activeFrom;
            this.activeTo = object.activeTo;

            this.modified = object.modified;
            this.previousState = object.previousState;
            this._syncCounter = object._syncCounter;

            this._groupedMessages = object._groupedMessages;
            this._userName = object._userName;
            this._routeInfo = object._routeInfo;
            this._routeName = object._routeName;
            this._messageLocationUri = object._messageLocationUri;
            this._response = object._response;
            this._audibleAlert = object._audibleAlert;
            this._cannedReply = object._cannedReply;
        }
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    getLegacySentMoment () {
        return this.sent && dayjs(dstCalculator.toLocalDateTime(this.sent)).format("LLL");
    }

    getSent () {
        return this.sent ? new Date(this.sent) : null;
    }

    getSentMoment () {
        return this.sent && dayjs(dstCalculator.toLocalDateTime(this.sent)).format("L LT");
    }

    getSentTime () {
        return this.sent && dayjs(dstCalculator.toLocalDateTime(this.sent)).format("LT");
    }

    getSentDate (includeToday = true) {
        if (typeof (this.sent) === "undefined") {
            return undefined;
        }
        const localDateTime = dstCalculator.toLocalDateTime(this.sent);
        const getFormattedDate = (date: Date) => dayjs(date).format("LL");

        const time = localDateTime.getTime();
        let endOfToday = new Date();
        const DAY = 24 * 60 * 60;
        const TWO_DAYS = DAY * 2;

        endOfToday.setHours(23);
        endOfToday.setMinutes(59);
        endOfToday.setSeconds(59);

        const today = endOfToday.getTime();
        const diff = (today - time) / 1000; // seconds

        if (diff < DAY) {
            return includeToday ? `Today ${ getFormattedDate(localDateTime) }` : getFormattedDate(localDateTime);
        }

        if (diff <  TWO_DAYS) {
            return includeToday ? `Yesterday ${ getFormattedDate(localDateTime) }` : getFormattedDate(localDateTime);
        }
        return getFormattedDate(localDateTime);
    }

    getAddressUri(): Promise<string> {
        return new Promise(resolve => {
            getBrowserGeoUri(this.messageContent?.latitude, this.messageContent?.longitude, this.messageContent?.address, resolve);
        });
    }

    getContent (showAsLink = true, keepNewLines = true): string | null {
        let msgContent = this.messageContent?.message;
        if (msgContent) {
            let message = parseMessage(msgContent).reduce((coll, phrase) => {
                if (phrase.link) {
                    if (showAsLink) {
                        if (phrase.geotabDriveLink) {
                            let link = phrase.link.replace(/^geotabdrive:\/\//, "");
                            return `${ coll }${ `<a class="geotabDriveLink" href="#${ link }" label="${ phrase.text }" target="_self">${ phrase.text }</a>` }`;
                        }
                        return `${ coll }${ `<a class="messageLink" href="${ phrase.link }" label="${ phrase.text }" target="_blank">${ phrase.text }</a>` }`;
                    }
                    return `${ coll }${ `[${ phrase.text }]` }`;
                }

                return `${ coll }${ phrase.text }`;
            }, "");

            if (keepNewLines) {
                message = message.replace(NEWLINE_REGEX, "<br/>");
            }
            return message;
        }
        return null;
    }

    isSendPending(): boolean {
        return this.modified === "Add";
    }
}